import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useExam } from "../context/ExamContext";
import {
  getExams,
  getProfile,
  getExamRegistration,
  verifyExamCode,
} from "../api/client";
import AppSidebar from "../pages/AppSidebar";

const ICON_BG = {
  "CMA USA": "#ed9b2f",
  ACCA: "#1c8a5a",
  EA: "#6f42c1",
};

const TOP_LEVEL_ITEMS = {
  cma1: {
    type: "exam",
    title: "CMA Part 1",
    examId: 1,
    program: "CMA USA",
    icon: "bi-award",
  },
  cma2: {
    type: "exam",
    title: "CMA Part 2",
    examId: 2,
    program: "CMA USA",
    icon: "bi-award",
  },
  accaKnowledge: {
    type: "level",
    title: "ACCA Knowledge Level",
    level: "acca_knowledge",
    program: "ACCA",
    icon: "bi-book",
  },
  accaSkills: {
    type: "level",
    title: "ACCA Skills Level",
    level: "acca_skills",
    program: "ACCA",
    icon: "bi-book",
  },
  accaProfessional: {
    type: "level",
    title: "ACCA Strategic Professional",
    level: "acca_professional",
    program: "ACCA",
    icon: "bi-mortarboard",
  },
  ea1: {
    type: "exam",
    title: "EA Part 1",
    examId: 6,
    program: "EA",
    icon: "bi-journal-text",
  },
  ea2: {
    type: "exam",
    title: "EA Part 2",
    examId: 7,
    program: "EA",
    icon: "bi-journal-text",
  },
  ea3: {
    type: "exam",
    title: "EA Part 3",
    examId: 8,
    program: "EA",
    icon: "bi-journal-text",
  },
};

function normalizeCourse(course) {
  if (!course) return null;

  if (typeof course === "number") {
    return {
      id: course,
      name: "",
    };
  }

  if (typeof course === "string") {
    return {
      id: null,
      name: course,
    };
  }

  return {
    id: course.id ?? course.course_id ?? null,
    name: course.name ?? "",
  };
}

function getExamId(exam) {
  return Number(exam?.id);
}

function isActiveExam(exam) {
  return exam?.is_active !== false;
}

export default function Home() {
  const navigate = useNavigate();

  const {
    currentUser,
    setSelectedCourse,
    setSelectedExam,
    setRegistrationId,
    setCriteriaAccepted,
    setPaymentStatus,
    setSchedule,
    logout,
  } = useExam();

  const [exams, setExams] = useState([]);
  const [registeredCourses, setRegisteredCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [scheduledExams, setScheduledExams] = useState([]);
  const [registrationStates, setRegistrationStates] = useState([]);
  const [verificationExam, setVerificationExam] = useState(null);
  const [verificationCode, setVerificationCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [verificationError, setVerificationError] = useState("");
  const [tileMessage, setTileMessage] = useState("");
  const [profileIsInstituteStudent, setProfileIsInstituteStudent] = useState(false);
  const [clockNow, setClockNow] = useState(() => Date.now());
  /*
   * ==========================================================
   * LOAD STUDENT PROFILE + EXAMS
   * ==========================================================
   */

  useEffect(() => {
    let mounted = true;

    const loadHomeData = async () => {
      setLoading(true);
      setError("");

      try {
        const [profileResponse, examsResponse] = await Promise.all([
          getProfile(),
          getExams(),
        ]);

        if (!mounted) return;

        const profileData = profileResponse?.data || {};

        // Use profile information as a fallback if the registration endpoint
        // does not include is_institute_student.
        const profileInstitute =
          profileData.institute ??
          profileData.institute_id ??
          profileData.student_profile?.institute ??
          profileData.student_profile?.institute_id ??
          null;

        setProfileIsInstituteStudent(
          profileData.is_institute_student === true ||
          Boolean(profileInstitute)
        );

        const courses = Array.isArray(profileData.courses)
          ? profileData.courses
          : [];

        setRegisteredCourses(courses);

        const examData = examsResponse?.data;
        let examList = [];

        if (Array.isArray(examData)) {
          examList = examData;
        } else if (
          examData &&
          Array.isArray(examData.results)
        ) {
          examList = examData.results;
        }

        setExams(examList);
        await loadScheduledExams(examList);
      } catch (err) {
        console.error("Home loading error:", err);

        if (mounted) {
          setError(
            "Could not load your courses and examinations."
          );
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadHomeData();

    return () => {
      mounted = false;
    };
  }, [profileIsInstituteStudent]);

  // Keep scheduled-exam availability current without requiring a page refresh.
  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setClockNow(Date.now());
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, []);

  /*
   * ==========================================================
   * DETERMINE REGISTERED PROGRAMS
   * ==========================================================
   */

  const registeredCourseObjects = useMemo(() => {
    return registeredCourses
      .map(normalizeCourse)
      .filter(Boolean);
  }, [registeredCourses]);

  const registeredCourseIds = useMemo(() => {
    return registeredCourseObjects
      .map((course) => course.id)
      .filter((id) => id !== null)
      .map(Number);
  }, [registeredCourseObjects]);

  const registeredCourseNames = useMemo(() => {
    return registeredCourseObjects
      .map((course) => String(course.name || "").toLowerCase())
      .filter(Boolean);
  }, [registeredCourseObjects]);

  /*
   * ==========================================================
   * PROGRAM REGISTRATION
   * ==========================================================
   */

  const isProgramRegistered = (program) => {
    const nameMatch = registeredCourseNames.some((name) => {
      if (program === "CMA USA") {
        return name.includes("cma");
      }

      if (program === "ACCA") {
        return name.includes("acca");
      }

      if (program === "EA") {
        return (
          name === "ea" ||
          name.includes("enrolled agent")
        );
      }

      return false;
    });

    if (nameMatch) return true;

    return exams.some((exam) => {
      if (!isActiveExam(exam)) return false;

      const examCourseId = Number(exam?.course);

      if (
        !Number.isNaN(examCourseId) &&
        registeredCourseIds.includes(examCourseId)
      ) {
        const examProgram = String(
          exam?.program || ""
        ).toLowerCase();

        if (program === "CMA USA") {
          return (
            examProgram.includes("cma") ||
            String(exam?.name || "")
              .toLowerCase()
              .includes("cma")
          );
        }

        if (program === "ACCA") {
          return (
            examProgram.includes("acca") ||
            String(exam?.name || "")
              .toLowerCase()
              .includes("acca") ||
            [
              "bt",
              "ma",
              "fa",
              "lw",
              "pm",
              "tx",
              "fr",
              "aa",
              "fm",
              "sbl",
              "sbr",
              "afm",
              "apm",
              "atx",
              "aaa",
            ].includes(
              String(exam?.name || "")
                .trim()
                .toLowerCase()
            )
          );
        }

        if (program === "EA") {
          return (
            examProgram.includes("ea") ||
            String(exam?.name || "")
              .toLowerCase()
              .includes("ea")
          );
        }
      }

      return false;
    });
  };

  /*
   * ==========================================================
   * HOME ITEMS
   * ==========================================================
   */

  const visibleItems = useMemo(() => {
    const items = [];

    if (isProgramRegistered("CMA USA")) {
      items.push(TOP_LEVEL_ITEMS.cma1);
      items.push(TOP_LEVEL_ITEMS.cma2);
    }

    if (isProgramRegistered("ACCA")) {
      items.push(TOP_LEVEL_ITEMS.accaKnowledge);
      items.push(TOP_LEVEL_ITEMS.accaSkills);
      items.push(TOP_LEVEL_ITEMS.accaProfessional);
    }

    if (isProgramRegistered("EA")) {
      items.push(TOP_LEVEL_ITEMS.ea1);
      items.push(TOP_LEVEL_ITEMS.ea2);
      items.push(TOP_LEVEL_ITEMS.ea3);
    }

    return items;
  }, [
    registeredCourseIds,
    registeredCourseNames,
    exams,
  ]);

  /*
   * ==========================================================
   * FIND ACTUAL EXAM
   * ==========================================================
   */

  const findExamById = (examId) => {
    return exams.find(
      (exam) => getExamId(exam) === Number(examId)
    );
  };

  /*
   * ==========================================================
   * DATE AND TIME HELPERS
   * ==========================================================
   */

  const getTodayISO = () => {
    const now = new Date();

    return `${now.getFullYear()}-${String(
      now.getMonth() + 1
    ).padStart(2, "0")}-${String(
      now.getDate()
    ).padStart(2, "0")}`;
  };

  const formatExamDate = (dateString) => {
    if (!dateString) return "Not scheduled";

    const date = new Date(`${dateString}T00:00:00`);

    if (Number.isNaN(date.getTime())) {
      return dateString;
    }

    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  const formatExamTime = (timeString) => {
    if (!timeString) return "";

    const [hours, minutes] = String(timeString).split(":");

    const date = new Date();
    date.setHours(Number(hours), Number(minutes), 0, 0);

    return date.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const isExamToday = (schedule) => {
    return Boolean(
      schedule?.exam_date &&
      String(schedule.exam_date).slice(0, 10) === getTodayISO()
    );
  };

  // Parse the saved schedule as local exam time. The backend remains the
  // authority and must also reject verification/start attempts outside this window.
  const getScheduleAvailability = (schedule) => {
    if (!schedule?.exam_date || !schedule?.start_time) {
      return { status: "not_ready", canVerify: false };
    }

    const examDate = String(schedule.exam_date).slice(0, 10);
    const startTime = String(schedule.start_time).slice(0, 8);
    const startAt = new Date(`${examDate}T${startTime}`);

    if (Number.isNaN(startAt.getTime())) {
      return { status: "not_ready", canVerify: false };
    }

    const now = new Date(clockNow);

    if (now.getTime() < startAt.getTime()) {
      return { status: "upcoming", canVerify: false, startAt };
    }

    if (schedule.end_time) {
      const endTime = String(schedule.end_time).slice(0, 8);
      const endAt = new Date(`${examDate}T${endTime}`);

      if (!Number.isNaN(endAt.getTime()) && now.getTime() > endAt.getTime()) {
        return { status: "ended", canVerify: false, startAt, endAt };
      }
    }

    return { status: "open", canVerify: true, startAt };
  };

  const isVerificationAvailable = (schedule) =>
    isExamToday(schedule) &&
    getScheduleAvailability(schedule).canVerify;

  const getScheduleAvailabilityMessage = (schedule) => {
    const availability = getScheduleAvailability(schedule);

    if (availability.status === "upcoming") {
      return `Your exam is scheduled for ${formatExamDate(
        schedule.exam_date
      )} at ${formatExamTime(
        schedule.start_time
      )}. Please come back at your scheduled time.`;
    }

    if (availability.status === "ended") {
      return "Your scheduled examination time has passed. Please contact the examination administrator.";
    }

    if (availability.status === "not_ready") {
      return "Your exam schedule is incomplete. Please contact the examination administrator.";
    }

    return "";
  };

  /*
   * ==========================================================
   * LOAD REGISTRATION STATES
   * ==========================================================
   */

  const loadScheduledExams = async (examList) => {
    const activeExams = (
      Array.isArray(examList) ? examList : []
    ).filter(isActiveExam);

    if (!activeExams.length) {
      setScheduledExams([]);
      setRegistrationStates([]);
      return;
    }

    const registrations = await Promise.all(
      activeExams.map(async (exam) => {
        try {
          const response = await getExamRegistration(exam.id);
          const data = response?.data || {};

          if (!data.registered) {
            return null;
          }

          const registrationStatus = String(
            data.status || ""
          ).toUpperCase();

          const isInstituteStudent =
            data.is_institute_student === true ||
            (
              data.is_institute_student == null &&
              profileIsInstituteStudent
            );

          const paymentStatus = String(
            data.payment_status || ""
          ).toUpperCase();

          const institutePaymentStatus = String(
            data.institute_payment_status || ""
          ).toUpperCase();

          return {
            exam,
            registrationId: data.registration_id,
            status: registrationStatus,
            criteriaAccepted: Boolean(data.criteria_accepted),
            isInstituteStudent,
            paymentStatus,
            institutePaymentStatus,
            schedule: data.schedule || null,
          };
        } catch (err) {
          console.error(
            `Unable to load registration for exam ${exam.id}:`,
            err?.response?.data || err
          );

          return null;
        }
      })
    );

    const validRegistrations = registrations.filter(Boolean);

    setRegistrationStates(validRegistrations);

    setScheduledExams(
      validRegistrations.filter(
        (registration) =>
          registration.schedule &&
          [
            "SCHEDULED",
            "VERIFICATION_PENDING",
          ].includes(registration.status)
      )
    );
  };

  /*
   * ==========================================================
   * OPEN EXAM VERIFICATION
   * ==========================================================
   */

  const openVerification = (scheduledExam) => {
    if (!isVerificationAvailable(scheduledExam.schedule)) {
      setTileMessage(getScheduleAvailabilityMessage(scheduledExam.schedule) ||
        "Verification is available only during your scheduled examination time.");
      return;
    }

    setTileMessage("");

    setSelectedCourse(scheduledExam.exam);
    setSelectedExam(scheduledExam.exam);
    setRegistrationId(scheduledExam.registrationId);
    setCriteriaAccepted(true);
    setPaymentStatus("SUCCESS");

    setSchedule({
      id: scheduledExam.schedule.id,
      scheduleId: scheduledExam.schedule.id,
      date: scheduledExam.schedule.exam_date,
      exam_date: scheduledExam.schedule.exam_date,
      start_time: scheduledExam.schedule.start_time,
      end_time: scheduledExam.schedule.end_time,
      time: scheduledExam.schedule.start_time,
    });

    setVerificationExam(scheduledExam);
    setVerificationCode("");
    setVerificationError("");
  };

  const closeVerification = () => {
    if (verifying) return;

    setVerificationExam(null);
    setVerificationCode("");
    setVerificationError("");
  };

  /*
   * ==========================================================
   * VERIFY OTP
   * ==========================================================
   */

  const handleVerificationSubmit = async (event) => {
    event.preventDefault();

    if (!verificationExam) return;

    const code = verificationCode.trim();

    if (!/^\d{6}$/.test(code)) {
      setVerificationError(
        "Verification code must be 6 digits."
      );
      return;
    }

    if (!isVerificationAvailable(verificationExam.schedule)) {
      setVerificationError(
        getScheduleAvailabilityMessage(verificationExam.schedule) ||
          "Verification is available only during your scheduled examination time."
      );
      return;
    }

    setVerifying(true);
    setVerificationError("");

    try {
      await verifyExamCode(
        verificationExam.exam.id,
        code
      );

      setVerificationExam(null);
      setVerificationCode("");

      navigate("/exam");
    } catch (err) {
      console.error(
        "EXAM VERIFICATION ERROR:",
        err?.response?.data || err
      );

      setVerificationError(
        err?.response?.data?.detail ||
          err?.response?.data?.message ||
          "Invalid verification code. Please check the code sent to your email and try again."
      );
    } finally {
      setVerifying(false);
    }
  };

  /*
   * ==========================================================
   * CLICK EXAM CARD
   * ==========================================================
   */

  
const handleItemClick = async (item) => {
  setTileMessage("");

  // ACCA syllabus tiles
  if (item.type === "level") {
    navigate(`/acca-syllabus/${item.level}`);
    return;
  }

  const exam = findExamById(item.examId);

  if (!exam) {
    setTileMessage(
      `${item.title} is currently unavailable. Please check whether the exam is active.`
    );
    return;
  }

  setError("");
  setSelectedCourse(exam);
  setSelectedExam(exam);

  try {
    const response = await getExamRegistration(exam.id);
    const data = response?.data || {};

    console.log("Exam registration response:", data);

    // A completed attempt must be handled before checking the old
    // registration status. Once its result is released, send the student
    // to Exam Criteria so the backend can create a fresh retake registration.
    // Until release, keep the student on the scoreboard/results flow.
    if (data.retake_required === true) {
      if (data.can_retake === true) {
        setRegistrationId(null);
        setCriteriaAccepted(false);
        setPaymentStatus(null);
        setSchedule(null);
        navigate("/exam-criteria");
      } else {
        navigate("/scoreboard");
      }
      return;
    }

    // Not registered yet: start with exam criteria.
    if (!data.registered) {
      setRegistrationId(null);
      setCriteriaAccepted(false);
      setPaymentStatus(null);
      setSchedule(null);
      navigate("/exam-criteria");
      return;
    }

    setRegistrationId(data.registration_id || null);
    setCriteriaAccepted(Boolean(data.criteria_accepted));
    setPaymentStatus(data.payment_status || null);

    if (data.schedule) {
      setSchedule({
        id: data.schedule.id,
        scheduleId: data.schedule.id,
        date: data.schedule.exam_date,
        exam_date: data.schedule.exam_date,
        start_time: data.schedule.start_time,
        end_time: data.schedule.end_time,
        time: data.schedule.start_time,
      });
    } else {
      setSchedule(null);
    }

    const registrationStatus = String(
      data.status || ""
    ).toUpperCase();

    const individualPaymentStatus = String(
      data.individual_payment_status ??
      data.payment_status ??
      ""
    ).toUpperCase();

    const institutePaymentStatus = String(
      data.institute_payment_status || ""
    ).toUpperCase();

    const isInstituteStudent =
      data.is_institute_student === true ||
      (
        data.is_institute_student == null &&
        profileIsInstituteStudent
      );

    // Criteria must be accepted first.
    if (!data.criteria_accepted) {
      navigate("/exam-criteria");
      return;
    }

    // Institute student workflow
    if (isInstituteStudent) {
      if (
        registrationStatus === "SUBMITTED" ||
        registrationStatus === "AUTO_SUBMITTED" ||
        registrationStatus === "TERMINATED"
      ) {
        navigate("/scoreboard");
        return;
      }

      if (
        registrationStatus === "READY" ||
        registrationStatus === "IN_PROGRESS"
      ) {
        navigate("/exam");
        return;
      }

      const institutePaymentApproved =
        institutePaymentStatus === "SUCCESS" ||
        (
          institutePaymentStatus === "" &&
          (
            individualPaymentStatus === "SUCCESS" ||
            registrationStatus === "PAID"
          )
        );

      if (!institutePaymentApproved) {
        setTileMessage(
          "Your institute payment is awaiting approval. You do not need to pay individually."
        );
        return;
      }

      if (
        registrationStatus === "SCHEDULED" ||
        registrationStatus === "VERIFICATION_PENDING"
      ) {
        const scheduledExam = scheduledExams.find(
          (entry) =>
            Number(entry.exam.id) === Number(exam.id)
        );

        if (scheduledExam && isVerificationAvailable(scheduledExam.schedule)) {
          openVerification(scheduledExam);
        } else if (scheduledExam) {
          setTileMessage(getScheduleAvailabilityMessage(scheduledExam.schedule) ||
            "Your institute has scheduled this exam. Please return during the scheduled examination time.");
        } else {
          setTileMessage(
            "Your institute has scheduled this exam. Please return during the scheduled examination time."
          );
        }

        return;
      }

      if (!data.schedule) {
        setTileMessage(
          "Your institute payment is approved. Please wait for your institute to schedule the examination."
        );
        return;
      }

      setTileMessage(
        "Your examination is not ready yet. Please contact your institute."
      );
      return;
    }

    // Regular student workflow: individual payment and scheduling.
    if (
      registrationStatus === "REGISTERED" ||
      registrationStatus === "PAYMENT_PENDING"
    ) {
      if (individualPaymentStatus === "SUCCESS") {
        navigate("/schedule");
      } else {
        navigate("/payment");
      }
      return;
    }

    if (registrationStatus === "PAID") {
      navigate("/schedule");
      return;
    }

    if (
      registrationStatus === "SCHEDULED" ||
      registrationStatus === "VERIFICATION_PENDING"
    ) {
      const scheduledExam = scheduledExams.find(
        (entry) =>
          Number(entry.exam.id) === Number(exam.id)
      );

      if (scheduledExam && isVerificationAvailable(scheduledExam.schedule)) {
        openVerification(scheduledExam);
      } else if (scheduledExam) {
        setTileMessage(getScheduleAvailabilityMessage(scheduledExam.schedule) ||
          "This exam is scheduled. Please return during the scheduled examination time.");
      } else {
        setTileMessage(
          "This exam is scheduled. Please return during the scheduled examination time."
        );
      }
      return;
    }

    if (
      registrationStatus === "READY" ||
      registrationStatus === "IN_PROGRESS"
    ) {
      navigate("/exam");
      return;
    }

    if (
      registrationStatus === "SUBMITTED" ||
      registrationStatus === "AUTO_SUBMITTED" ||
      registrationStatus === "TERMINATED"
    ) {
      navigate("/scoreboard");
      return;
    }

    setTileMessage(
      `Unable to determine the examination status: ${
        registrationStatus || "UNKNOWN"
      }. Please refresh and try again.`
    );
  } catch (err) {
    console.error(
      "EXAM TILE NAVIGATION ERROR:",
      err?.response?.data || err
    );

    setTileMessage(
      err?.response?.data?.detail ||
      err?.response?.data?.message ||
      "Unable to load your examination details. Please try again."
    );
  }
};


  /*
   * ==========================================================
   * LOGOUT
   * ==========================================================
   */

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  /*
   * ==========================================================
   * FIRST NAME
   * ==========================================================
   */

  const firstName = currentUser?.full_name
    ? currentUser.full_name.split(" ")[0]
    : currentUser?.fullName
    ? currentUser.fullName.split(" ")[0]
    : "Student";

  /*
   * ==========================================================
   * UI
   * ==========================================================
   */

  return (
    <div className="oes-page-full">
      <div className="oes-home-shell d-flex flex-row overflow-hidden">
        <AppSidebar />

        <div className="flex-grow-1 p-3 p-md-4">
          {/* HEADER */}

          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2 className="h5 mb-0">
              Welcome, {firstName}!
            </h2>

            <div className="d-flex align-items-center gap-3">
              <button
                type="button"
                className="btn btn-link p-0 text-decoration-none position-relative"
                onClick={() => {
                  const todayExam = scheduledExams.find(
                    (item) => isExamToday(item.schedule)
                  );

                  if (todayExam) {
                    if (isVerificationAvailable(todayExam.schedule)) {
                      openVerification(todayExam);
                    } else {
                      setTileMessage(getScheduleAvailabilityMessage(todayExam.schedule) ||
                        "Please return during your scheduled examination time.");
                    }
                  }
                }}
                title="Scheduled examinations"
              >
                <i className="bi bi-bell fs-5 text-secondary" />

                {scheduledExams.length > 0 && (
                  <span
                    className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
                    style={{ fontSize: "0.55rem" }}
                  >
                    {scheduledExams.length}
                  </span>
                )}
              </button>

              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={handleLogout}
              >
                <i className="bi bi-box-arrow-right me-1" />
                Logout
              </button>
            </div>
          </div>

          {/* SCHEDULED EXAMS */}

          {scheduledExams.length > 0 && (
            <div className="mb-3">
              {scheduledExams.map((scheduledExam) => {
                const today = isExamToday(scheduledExam.schedule);
                const scheduleAvailability = getScheduleAvailability(
                  scheduledExam.schedule
                );
                const canVerify = today && scheduleAvailability.canVerify;

                return (
                  <div
                    key={`${scheduledExam.exam.id}-${scheduledExam.registrationId}`}
                    className="d-flex align-items-center justify-content-between p-2 px-3 border rounded bg-white shadow-sm"
                  >
                    <div className="d-flex align-items-center gap-2">
                      <i
                        className={`bi ${
                          canVerify
                            ? "bi-bell-fill text-primary"
                            : "bi-calendar-check text-secondary"
                        }`}
                      />

                      <div>
                        <div className="fw-semibold small">
                          {scheduledExam.exam.name ||
                            scheduledExam.exam.title ||
                            "Examination"}
                        </div>

                        <div
                          className="text-muted"
                          style={{ fontSize: "12px" }}
                        >
                          {formatExamDate(
                            scheduledExam.schedule.exam_date
                          )}
                          {" • "}
                          {formatExamTime(
                            scheduledExam.schedule.start_time
                          )}
                          {" - "}
                          {formatExamTime(
                            scheduledExam.schedule.end_time
                          )}
                        </div>
                      </div>
                    </div>

                    {canVerify ? (
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => openVerification(scheduledExam)}
                      >
                        Verify
                      </button>
                    ) : (
                      <div className="text-end">
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          disabled
                          title={
                            scheduleAvailability.status === "upcoming"
                              ? "Verification opens at the scheduled start time."
                              : "Verification is not available at this time."
                          }
                        >
                          Verify
                        </button>
                        <div className="small text-muted mt-1">
                          {scheduleAvailability.status === "upcoming"
                            ? "Come back at your scheduled time"
                            : scheduleAvailability.status === "ended"
                            ? "Scheduled time passed"
                            : "Scheduled"}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* INSTITUTE PAYMENT / SCHEDULING STATUS */}

          {registrationStates
            .filter(
              (registration) =>
                registration.isInstituteStudent &&
                registration.criteriaAccepted &&
                !registration.schedule &&
                ![
                  "READY",
                  "IN_PROGRESS",
                  "SUBMITTED",
                  "AUTO_SUBMITTED",
                  "TERMINATED",
                ].includes(registration.status)
            )
            .map((registration) => {
              const paymentApproved =
                registration.institutePaymentStatus === "SUCCESS" ||
                registration.paymentStatus === "SUCCESS" ||
                registration.status === "PAID";

              return (
                <div
                  key={`institute-status-${registration.exam.id}-${registration.registrationId}`}
                  className="alert alert-info d-flex align-items-start gap-2 py-2 mb-3 small"
                >
                  <i
                    className={`bi ${
                      paymentApproved
                        ? "bi-calendar-event"
                        : "bi-hourglass-split"
                    } fs-5`}
                  />

                  <div>
                    <div className="fw-semibold">
                      {registration.exam.name ||
                        registration.exam.title ||
                        "Examination"}
                    </div>

                    <div>
                      {paymentApproved
                        ? "Institute payment approved. Waiting for your institute to schedule your examination."
                        : "Waiting for your institute to approve payment for this examination. You do not need to pay individually."}
                    </div>
                  </div>
                </div>
              );
            })}

          <span className="badge oes-course-badge mb-4">
            Courses
          </span>

          {/* LOADING */}

          {loading && (
            <div className="text-muted">
              Loading courses...
            </div>
          )}
          
{tileMessage && (
  <div
    className="alert alert-info py-2 small mb-3"
    role="status"
  >
    {tileMessage}
  </div>
)}


          {/* ERROR */}

          {error && (
            <div className="alert alert-warning py-2 small">
              {error}
            </div>
          )}

          {/* NO REGISTERED COURSE */}

          {!loading &&
            !error &&
            visibleItems.length === 0 && (
              <div className="alert alert-info py-2 small">
                No registered courses are available.
              </div>
            )}

          {/* COURSE CARDS */}

          {!loading &&
            !error &&
            visibleItems.length > 0 && (
              <div className="row g-3">
                {visibleItems.map((item) => (
                  <div
                    className="col-6 col-sm-4 col-lg-3"
                    key={item.title}
                  >
                    <button
                      type="button"
                      className="course-tile border-0 text-start w-100"
                      onClick={() => handleItemClick(item)}
                      aria-label={`Open ${item.title}`}
                      style={{
                        cursor: "pointer",
                        font: "inherit",
                      }}
                    >
                      <div
                        className="course-icon"
                        style={{
                          background:
                            ICON_BG[item.program] || "#2f6fed",
                        }}
                      >
                        <i className={`bi ${item.icon}`} />
                      </div>

                      <div className="fw-semibold small">
                        {item.title}
                      </div>
                    </button>
                  </div>
                ))}
              </div>
            )}
        </div>
      </div>

      {/* OTP VERIFICATION MODAL */}

      {verificationExam && (
        <div
          className="modal d-block"
          tabIndex="-1"
          role="dialog"
          aria-modal="true"
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.55)",
          }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow">
              <div className="modal-header">
                <div>
                  <h5 className="modal-title mb-1">
                    <i className="bi bi-shield-lock me-2 text-primary" />
                    Verify Examination
                  </h5>

                  <div className="small text-muted">
                    {verificationExam.exam.name ||
                      verificationExam.exam.title ||
                      "Examination"}
                  </div>
                </div>

                <button
                  type="button"
                  className="btn-close"
                  onClick={closeVerification}
                  disabled={verifying}
                />
              </div>

              <form onSubmit={handleVerificationSubmit}>
                <div className="modal-body">
                  <div className="alert alert-info small">
                    Enter the 6-digit verification code sent to
                    your registered email address.
                  </div>

                  <label
                    htmlFor="exam-verification-code"
                    className="form-label fw-semibold"
                  >
                    Verification Code
                  </label>

                  <input
                    id="exam-verification-code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    className="form-control form-control-lg text-center"
                    placeholder="Enter 6-digit code"
                    value={verificationCode}
                    onChange={(event) =>
                      setVerificationCode(
                        event.target.value
                          .replace(/\D/g, "")
                          .slice(0, 6)
                      )
                    }
                    disabled={verifying}
                    autoFocus
                  />

                  {verificationError && (
                    <div className="alert alert-danger small mt-3 mb-0">
                      {verificationError}
                    </div>
                  )}
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={closeVerification}
                    disabled={verifying}
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={
                      verifying ||
                      verificationCode.length !== 6 ||
                      !isVerificationAvailable(verificationExam.schedule)
                    }
                  >
                    {verifying ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                        />
                        Verifying...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-circle me-2" />
                        Verify &amp; Start
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}